import { Scaling } from "./Scaling";
import { lodash, NumberUtils, StringUtils, UriHelper, Preloader } from "neo-common";
import { EventData } from "neo-games/Engine/Plugins/EventTracker/EventTracker";
import { NotificationManager } from "../Managers/NotificationManager";
import { RouletteActions } from "../Channels/Common/Actions/RouletteActions";
import { UserAction } from "../HTMLComponents/UserAction";
import { DataManager } from "../Managers/DataManager";
import { GameApp } from "../GameApp";
import { GameData } from "../Channels/Common/Datas/GameData";
import { PlayList } from "../Components/Sounds/PlayList";
import { SongAttributes } from "../Components/Sounds/Song";
import { AppContext } from "../../Common/AppContext";

export class CustomUtils {
    private static sessionEvents: Array<string> = [];

    private static actionsCounter: { [actionId: string]: number } = {};

    private static actionsCounterTemp: { [actionId: string]: number } = {};

    private static loadedSounds: Array<string> = [];

    private static voiceOverGroupId: string = "VoiceOver";

    public static soundsPath: string;

    public static incrementActionCount(actionId: string): void {
        if (CustomUtils.actionsCounter[actionId] == null) {
            CustomUtils.actionsCounter[actionId] = 1;
        } else {
            if (actionId == RouletteActions.ActionStatisticsToggle) {
                CustomUtils.actionsCounter[actionId] = CustomUtils.actionsCounter[actionId] == 1 ? 0 : 1;
                return;
            }

            CustomUtils.actionsCounter[actionId] += 1;
        }

        if (actionId == "RACETRACK_BET") {
            CustomUtils.actionsCounter["RACETRACK_NEIGHBOURS"] = parseInt(
                DataManager.getDataById(GameData.DataRacetrackNeighbours),
            );
        }

        if (actionId == RouletteActions.ActionBetBack || actionId == RouletteActions.ActionBetReset) {
            CustomUtils.actionsCounter[RouletteActions.ActionRebet] = 0;
        }

        if (actionId == UserAction.ActionAmbientSoundToggle) {
            CustomUtils.actionsCounter[UserAction.ActionAmbientSoundToggle] = DataManager.AmbientSoundEnabled ? 1 : 0;
        }

        if (actionId == UserAction.ActionCommentarySoundToggle) {
            CustomUtils.actionsCounter[UserAction.ActionCommentarySoundToggle] = DataManager.CommentarySoundEnabled
                ? 1
                : 0;
        }

        if (actionId == UserAction.ActionCommentaryGenderToggle) {
            CustomUtils.actionsCounter[UserAction.ActionCommentaryGenderToggle] = DataManager.CommentaryGender ? 1 : 0;
        }

        if (actionId == UserAction.ActionEffectsSoundToggle) {
            CustomUtils.actionsCounter[UserAction.ActionEffectsSoundToggle] = DataManager.EffectsSoundEnabled ? 1 : 0;
        }
    }

    public static resetActionsCounter(): void {
        for (let key in CustomUtils.actionsCounter) {
            CustomUtils.actionsCounter[key] = 0;
        }
    }

    private static updateGameRoundsTempData(): void {
        CustomUtils.actionsCounterTemp = {};

        for (let key in CustomUtils.actionsCounter) {
            CustomUtils.actionsCounterTemp[key] = CustomUtils.actionsCounter[key];
        }
    }

    private static getUpdatedGameRoundsData(): { [id: string]: number } {
        let result: { [actionId: string]: number } = {};

        for (let key in CustomUtils.actionsCounter) {
            if (CustomUtils.actionsCounter[key] !== CustomUtils.actionsCounterTemp[key]) {
                result[key] = CustomUtils.actionsCounter[key];
            }
        }

        return result;
    }

    public static sendRouletteActionsData(): void {
        CustomUtils.sendTrackingEvent("RouletteData", "data", null, CustomUtils.getUpdatedGameRoundsData());
        CustomUtils.updateGameRoundsTempData();
        CustomUtils.resetActionsCounter();
    }

    public static sendTrackingEvent(
        eventType: string,
        label?: string,
        value?: number,
        customData?: any,
        onlyOnce: boolean = false,
    ): void {
        if (onlyOnce && CustomUtils.checkSessionEvent(eventType)) {
            return;
        }

        if (!CustomUtils.enabledCustomEvents("RouletteData")) {
            return;
        }

        const event = CustomUtils.createTrackedEvent(eventType, label, value, customData);
        NotificationManager.sendMessage({
            application: "EventTrackingServer",
            service: "EventTracker",
            action: "LogEvent",
            data: { events: [event] },
        });
    }

    private static enabledCustomEvents(customEvent: string): boolean {
        const config = AppContext.gameConfiguration.EventTrackerEnabledEvents;

        if (config == "") {
            return false;
        }

        if (config == "*") {
            return true;
        }

        const splittedConfig = config.split(" ");
        if (splittedConfig.indexOf("CustomEvents") > -1) {
            return true;
        }

        for (let i = 0; i < splittedConfig.length; i++) {
            let category = splittedConfig[i].split(":");
            let categoryData: Array<string> = [];

            if (category[0] != "CustomEvents") {
                continue;
            }

            if (category.length === 1) {
                return true;
            }

            if (category.length === 2) {
                categoryData = category[1].split("|");
            }

            if (categoryData.indexOf(customEvent) > -1) {
                return true;
            }
        }

        return false;
    }

    private static checkSessionEvent(eventType: string): boolean {
        if (CustomUtils.sessionEvents.indexOf(eventType) == -1) {
            CustomUtils.sessionEvents.push(eventType);
            return false;
        } else {
            return true;
        }
    }

    private static createTrackedEvent(eventType: string, label?: string, value?: number, customData?: any): EventData {
        let eventData = {
            eventType: eventType,
        } as EventData;

        if (!StringUtils.isNullOrEmpty(label)) {
            eventData.label = label;
        }

        if (value != null) {
            eventData.value = value;
        }

        if (customData != null) {
            eventData.customData = customData;
        }

        return eventData;
    }

    public static getBlendMode(blendMode: number): number {
        if (Scaling.IsIOs) {
            if (blendMode == 1) {
                blendMode = 3;
            }
        }

        return blendMode;
    }

    public static updateSpineData(spineData: JSON): JSON {
        if (Scaling.IsIOs) {
            if (spineData != null) {
                let spineTextData = JSON.stringify(spineData);
                spineTextData = spineTextData.split('"blend":"additive"').join('"blend":"screen"');
                spineData = JSON.parse(spineTextData);
            }
        }

        return spineData;
    }

    public static abbreviateNumber(value: number): string {
        let result = value.toString();

        if (value >= 1000) {
            const suffixes = ["", "k", "m", "b", "t"];
            const suffixIndex = Math.floor(Math.floor(value).toString().length / 3);
            let shortValue = "";

            for (let precision = 2; precision >= 1; precision--) {
                shortValue = parseFloat(
                    suffixIndex != 0 ? NumberUtils.devide(value, Math.pow(1000, suffixIndex)) : result,
                ).toString();
                const dotLessShortValue = shortValue.replace(/[^a-zA-Z 0-9]+/g, "");
                if (dotLessShortValue.length <= 2) {
                    break;
                }
            }

            if (parseFloat(shortValue) % 1 != 0) {
                shortValue = parseFloat(shortValue).toFixed(1);
            }

            result = shortValue + suffixes[suffixIndex];
        }

        return result;
    }

    public static soundLazyLoad(lang: string): void {
        DataManager.NarratorLanguageLoaded = false;
        let language = lang.toUpperCase();
        let jsonPath = CustomUtils.soundsPath;

        if (AppContext.gameConfiguration.NarratorLanguages.indexOf(language) == -1) {
            language = "EN";
        }

        jsonPath = jsonPath.replace(new RegExp("{LANG}", "g"), language);
        const audioPath = jsonPath.replace(".json", ".{ogg,mp3}");
        const id = `SND_language_${language}`;

        if (CustomUtils.loadedSounds.indexOf(id) == -1) {
            CustomUtils.loadedSounds.push(id);
        } else {
            CustomUtils.updateVoiceLanguage(id);
            return;
        }

        const loader = new PIXI.loaders.Loader();
        loader.add(`SND_language_${language}_meta`, jsonPath);
        loader.add(`SND_language_${language}`, audioPath);

        loader.pre(CustomUtils.handleBeforeLoad);
        loader.on("error" /*PIXI_EVENTS.Error*/, CustomUtils.loaderOnError);
        loader.once("complete" /*PIXI_EVENTS.Complete*/, CustomUtils.loaderOnComplete);

        loader.load();
    }

    private static handleBeforeLoad(resource: PIXI.loaders.Resource, next: Function): void {
        const buildVersion = Preloader.getVersionId();

        if (StringUtils.isNullOrEmpty(buildVersion)) {
            next();
            return;
        }

        resource.url = UriHelper.setTimeStamp(resource.url, buildVersion);

        next();
    }

    private static loaderOnError(error: Error, loader: PIXI.loaders.Loader, resource: PIXI.loaders.Resource): void {
        //ToDo
        loader.reset();
    }

    private static loaderOnComplete(
        loader: PIXI.loaders.Loader,
        resources: { [id: string]: PIXI.loaders.Resource },
    ): void {
        let sound = "";
        for (let key in loader.resources) {
            if (key.indexOf("_meta") !== -1) {
                sound = key.substr(0, key.length - 5);

                if (PIXI.sound.exists(sound)) {
                    let soundSprite: PIXI.sound.Sound = PIXI.sound.find(sound);

                    if (lodash.isEmpty(soundSprite.sprites)) {
                        soundSprite.addSprites(loader.resources[key].data.spritemap);
                    }
                }
            }
        }

        if (StringUtils.isNullOrEmpty(sound)) {
            return;
        }

        CustomUtils.updateVoiceLanguage(sound);
        DataManager.NarratorLanguageLoaded = true;
        loader.reset();
    }

    private static updateVoiceLanguage(langSoundId: string): void {
        const songs = PlayList.getGroupSongs(CustomUtils.voiceOverGroupId);

        for (let key in songs) {
            const song = songs[key];
            const sound = PIXI.sound.find(langSoundId);

            if (sound != null) {
                song.updateSound(sound);
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Utils/CustomUtils.ts
 **/